import { Link } from 'react-router-dom';
import { POSTER_POINTS } from '@/content/site-content';
import { buttonClasses } from '@/components/ui/button-styles';
import { ArrowRightIcon, CheckIcon } from '@/components/ui/Icons';

/** Progress ring for the mock-up. 75% complete, matching the design. */
function ProgressRing() {
  const radius = 62;
  const circumference = 2 * Math.PI * radius;

  return (
    <svg viewBox="0 0 160 160" className="size-40" aria-hidden="true">
      <circle cx="80" cy="80" r={radius} fill="none" stroke="var(--color-slate-200)" strokeWidth="14" />
      <circle
        cx="80"
        cy="80"
        r={radius}
        fill="none"
        stroke="var(--color-safe-500)"
        strokeWidth="14"
        strokeLinecap="round"
        strokeDasharray={`${circumference * 0.75} ${circumference}`}
        transform="rotate(-90 80 80)"
      />
      <text
        x="80"
        y="76"
        textAnchor="middle"
        className="fill-safe-600 text-[1.6rem] font-extrabold"
      >
        75%
      </text>
      <text x="80" y="95" textAnchor="middle" className="fill-ink-soft text-[0.7rem]">
        Task Completed
      </text>
    </svg>
  );
}

function PosterMock() {
  return (
    <div
      aria-hidden="true"
      className="relative mx-auto grid aspect-square w-full max-w-md place-items-center"
    >
      <span className="absolute inset-16 rounded-full border border-dashed border-line" />
      <ProgressRing />

      <div className="absolute top-0 left-0 w-40 rounded-xl bg-white p-2.5 shadow-soft ring-1 ring-line">
        <p className="text-[0.65rem] font-bold text-ink">Post a Job</p>
        <p className="mt-1.5 flex items-center gap-1.5 text-[0.6rem] text-ink-soft">
          <span>📖</span> Tutoring
        </p>
        <p className="mt-1 flex items-center gap-1.5 text-[0.6rem] text-ink-soft">
          <span>🎟️</span> Event Support
        </p>
        <p className="mt-1 flex items-center gap-1.5 text-[0.6rem] text-ink-soft">
          <span>🐾</span> Pet Care
        </p>
      </div>

      <p className="absolute top-2 right-0 w-40 rounded-xl bg-white p-2.5 text-[0.6rem] leading-snug text-ink-soft shadow-soft ring-1 ring-line">
        <span className="font-bold text-ink">Karen,</span> how is everything going? Are we all set
        for tomorrow&rsquo;s event?
      </p>

      <p className="absolute bottom-1/4 left-0 w-36 rounded-xl bg-white p-2.5 text-[0.6rem] text-ink-soft shadow-soft ring-1 ring-line">
        Anyone available to help with this?
      </p>

      <p className="absolute right-0 bottom-2 w-36 rounded-xl bg-white p-2.5 text-[0.6rem] text-ink-soft shadow-soft ring-1 ring-line">
        I am done with my part - over to you!
      </p>

      <span className="absolute top-1/4 left-1/2 inline-flex items-center gap-1 rounded-md bg-white px-1.5 py-0.5 text-[0.55rem] font-bold text-safe-700 shadow-soft">
        <CheckIcon className="size-2.5" strokeWidth={3} />
        Verified
      </span>
    </div>
  );
}

export function PostersSection() {
  return (
    <section id="for-posters" className="container-page scroll-mt-28 pb-16 lg:pb-20">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <div className="order-2 lg:order-1">
          <PosterMock />
        </div>

        <div className="order-1 lg:order-2">
          <h2 className="text-3xl font-extrabold sm:text-4xl">For Job Posters</h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-ink-soft">
            Need reliable local help - tutoring, yard work, event support, pet care? Post a job and
            reach verified, motivated teens and young adults, with safety checks built into every
            step.
          </p>

          <ul className="mt-7 space-y-3">
            {POSTER_POINTS.map((point) => (
              <li key={point} className="flex items-start gap-3 text-sm text-ink">
                <span className="mt-px grid size-5 shrink-0 place-items-center rounded-full bg-brand-600 text-white">
                  <CheckIcon className="size-3" strokeWidth={3} />
                </span>
                {point}
              </li>
            ))}
          </ul>

          <Link to="/register" className={buttonClasses('primary', 'md', 'mt-8')}>
            Post a job
            <ArrowRightIcon className="size-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
